import React, { useState } from 'react'
import { Toaster } from 'react-hot-toast'
import { Breadcrumb } from '../../../../../components/ui/breadcrumb'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import axios from '../../../../../lib/axios'
import useAuth from '../../../../../hooks/useAuth'
import { Tablist, TablistItem } from '../../../../../components/ui/tablist'
import Denom from './detail/Denom'
import { isInterface } from '../../../../../lib/utils'
import SpinnerFeedback from '../../../../../components/portal/feedback/SpinnerFeedback'
import ErrorFeedback from '../../../../../components/portal/feedback/ErrorFeedback'
import { createDefaultKassaDenom } from '../../../../../lib/fetch/kassaDenom'
import { useTranslation } from 'react-i18next'

const Detail = () => {
    const { kassaid } = useParams()
    const { auth } = useAuth()
    const { t } = useTranslation()
    const navigate = useNavigate()
    const location = useLocation()

    const kassaFromState: Kassa | null = location.state?.kassa ?? null

    const [selected_subpage, setselected_subpage] = useState<'denom' | 'order'>('denom')

    const { data: kassa, isFetching: kassa_isFetching } = useQuery<Kassa | null>({
        queryKey: [`kassa_${kassaid}`],
        queryFn: async () => {
            try {
                const { data } = await axios.get('kassa/get', {
                    params: { token: auth.data?.token, kassaid: parseFloat(kassaid || '0') }
                })
                if (data.errorcode !== 0) { throw new Error(data.message) }
                const obj = Array.isArray(data.object) ? data.object[0] : data.object
                return obj ?? null
            } catch (error) {
                console.log(error)
                return null
            }
        }
    })

    const { data: denom, isFetching: denom_isFetching, refetch: refetch_denom } = useQuery<KassaDenom | null>({
        queryKey: [`kassa_denom_${kassaid}`],
        queryFn: async () => {
            const kid = parseFloat(kassaid || '0')
            try {
                const { data } = await axios.get('kassa/denom/search', {
                    params: { token: auth.data?.token, kassaid: kid }
                })
                console.log(`[SERVER RESPONSE: kassa/denom/search] kassaid=${kid}`, data)
                const candidates = [
                    data,
                    data?.object,
                    data?.object?.[0],
                    data?.object?.[0]?.denom,
                    data?.denom,
                ]
                const raw = candidates.find((c) => c && Array.isArray(c.coins) && Array.isArray(c.notes)) ?? null

                if (raw && Array.isArray(raw.coins) && Array.isArray(raw.notes)) {
                    const mapItem = (item: any): KDenom => ({
                        kassaid: kid,
                        id:      item.apikassadenomid ?? item.id ?? 0,
                        coupure: item.coupure,
                        minqty:  item.minqty ?? 0,
                        maxqty:  item.maxqty ?? 0,
                        perqty:  item.perquantity ?? item.perqty ?? 0,
                    })
                    const coins = raw.coins.filter((c: any) => c.coupure !== 0).map(mapItem)
                    const notes = raw.notes.filter((n: any) => n.coupure !== 0).map(mapItem)
                    // Lege arrays → val terug op de defaults zodat de pagina nooit leeg is.
                    if (coins.length > 0 || notes.length > 0) {
                        coins.sort((a: KDenom, b: KDenom) => b.coupure - a.coupure)
                        notes.sort((a: KDenom, b: KDenom) => b.coupure - a.coupure)
                        return {
                            kassaid:       kid,
                            ordercoins:    !!raw.ordercoins,
                            ordernotes:    !!raw.ordernotes,
                            announcecoins: !!raw.announcecoins,
                            announcenotes: !!raw.announcenotes,
                            coins,
                            notes,
                        }
                    }
                }

                const defaultDenom = await createDefaultKassaDenom(kid, auth)
                defaultDenom.coins.sort((a, b) => b.coupure - a.coupure)
                defaultDenom.notes.sort((a, b) => b.coupure - a.coupure)
                return defaultDenom
            } catch (error) {
                console.log(error)
                try {
                    const defaultDenom = await createDefaultKassaDenom(kid, auth)
                    defaultDenom.coins.sort((a, b) => b.coupure - a.coupure)
                    defaultDenom.notes.sort((a, b) => b.coupure - a.coupure)
                    return defaultDenom
                } catch {
                    return null
                }
            }
        }
    })

    const activeCn = (kassa ?? kassaFromState)?.cn

    return (
        <>
            <Toaster />
            <div className='overflow-auto relative w-full p-4 md:p-8'>
                <div className='flex flex-col'>
                    <Breadcrumb items={[
                        { title: <p onClick={() => { navigate('/instellingen') }} className='cursor-pointer'>{t('settings.title')}</p> },
                        { title: <p onClick={() => { navigate('..') }} className='cursor-pointer'>{t('settings.kassa_settings_title')}</p> },
                        { title: `${t('settings.kassa')} ${(kassa ?? kassaFromState)?.omschrijving || (kassa ?? kassaFromState)?.cn || kassaid}` }
                    ]} />
                    <h1 className='text-2xl font-semibold'>{t('settings.kassa_settings_title')}</h1>
                </div>

                <div className='flex border flex-col gap-4 rounded-sm p-4 mt-4 md:mt-8'>
                    <h2 className='text-lg'>{t('settings.title_detail')}</h2>
                    <div className='grid grid-cols-[repeat(auto-fit,minmax(250px,1fr))] gap-2'>
                        <div className='flex gap-2'>
                            <p className='font-semibold'>{t('settings.kassa_id')}</p>
                            <p className='text-neutral-500'>{kassaid}</p>
                        </div>
                        {kassa && (
                            <div className='flex gap-2'>
                                <p className='font-semibold'>{t('settings.kassa')}</p>
                                <p className='text-neutral-500'>{kassa.omschrijving || kassa.cn}</p>
                            </div>
                        )}
                    </div>
                </div>

                <div className='flex border flex-col gap-4 rounded-sm py-4 mt-4 md:mt-8'>
                    <Tablist value={selected_subpage} onValueChange={(value) => { setselected_subpage(value as 'denom' | 'order') }}>
                        <TablistItem value='denom'>{t('settings.denom')}</TablistItem>
                        <TablistItem value='order'>{t('settings.order')}</TablistItem>
                    </Tablist>

                    <div className='px-4'>
                        {selected_subpage === 'denom' && (
                            <>
                                {isInterface(denom, {} as KassaDenom)
                                    ? <Denom denom={denom} refetch={() => { refetch_denom() }} cn={activeCn} />
                                    : <>{denom_isFetching ? <SpinnerFeedback /> : <ErrorFeedback />}</>
                                }
                            </>
                        )}

                        {selected_subpage === 'order' && (
                            <>
                                {isInterface(denom, {} as KassaDenom)
                                    ? <Denom denom={denom} refetch={() => { refetch_denom() }} cn={activeCn} orderOnly />
                                    : <>{denom_isFetching ? <SpinnerFeedback /> : <ErrorFeedback />}</>
                                }
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    )
}

export default Detail
